import React, { useState } from 'react'

import { ButtonClean, Icon, Tooltip, useCopyToClipboard } from '@fourthwall/components'
import { DuplicateIcon, PreviewOnIcon, TickIcon } from '@fourthwall/icons'

import * as S from './CredentialDisplay.styled'

type CredentialDisplayProps = {
  label: string
  value?: string
  copyable?: boolean
  onReveal?: () => Promise<string>
  maxDisplayLength?: number
}

export const CredentialDisplay = ({
  label,
  value,
  copyable = false,
  onReveal,
  maxDisplayLength = 50,
}: CredentialDisplayProps) => {
  const { isCopied, copyToClipboard } = useCopyToClipboard()
  const [revealed, setRevealed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [finalValue, setFinalValue] = useState<string | undefined>(value)
  const revealable = !!onReveal

  const handleReveal = async () => {
    if (onReveal) {
      setLoading(true)
      try {
        const secret = await onReveal()
        setFinalValue(secret)
        setRevealed(true)
      } finally {
        setLoading(false)
      }
    }
  }

  const displayValue =
    revealable && !revealed ? '••••••••••••••••••••••••' : finalValue || 'No value'

  const truncatedDisplayValue =
    displayValue.length > maxDisplayLength
      ? `${displayValue.substring(0, maxDisplayLength)}...`
      : displayValue

  return (
    <S.Container>
      <S.HMACContainer>
        <S.HMACKeyLabel>{label}: </S.HMACKeyLabel>
        <div>{truncatedDisplayValue || `No ${label}`}</div>
        {copyable && finalValue && (
          <div>
            {isCopied ? (
              <Tooltip reference={<Icon component={TickIcon} height={12} />}>Copied!</Tooltip>
            ) : (
              <Tooltip
                reference={
                  <ButtonClean
                    label=""
                    icon={DuplicateIcon}
                    onClick={() => copyToClipboard(finalValue)}
                  />
                }
              >
                Copy
              </Tooltip>
            )}
          </div>
        )}
        {revealable && !revealed && (
          <div>
            <Tooltip
              reference={
                <ButtonClean
                  label="Reveal"
                  icon={PreviewOnIcon}
                  onClick={handleReveal}
                  disabled={loading}
                />
              }
            >
              {loading ? 'Loading...' : 'Reveal'}
            </Tooltip>
          </div>
        )}
      </S.HMACContainer>
    </S.Container>
  )
}
